// flowToolkit — the toolkit the three account flows share (contract 07 §8). `AccountBootstrapFlow`
// (ORP-18635) and the two switch flows (ORP-18636) each carried their own copy: ORP-18636 was scoped
// NOT to modify the boot flow, so the duplication was accepted and documented in-code as "KNOWN
// DUPLICATION (deliberate, scoped)". The v3 analysis flags exactly this shape as legacy debt
// (`docs/analysis/07-account-and-workspace.analysis.md` §2 "Dead / duplicated code" — "Duplicated
// pick-workspace: `BootstrapV2Flow.pickWorkspaceFromStore` vs `workspaceStore.getWorkspaces`
// implement the same order twice"), so v4 does not leave it standing.
//
// INTERNAL: an implementation detail of `flows/`, deliberately NOT exported from
// `features/account/index.ts` — the public surface stays the three flow classes (contract §4).
//
// A PURE refactor: every member below is the flows' own code, moved verbatim. What the flows do NOT
// share stays with them — the boot flow's DEFERRED compositions (extra fetch + `isCurrentWorkspace`
// staleness guard) and the switch's EAGER dispatch are two behaviours, not one to collapse. Nothing
// here writes `session.status` (`sessionStore` remains the single writer), classifies an error by
// English prose, or logs anyone out.
import type { UserResponse } from '../models/account.types'
import type { BootstrapSection, MappedBootstrapResult } from '../models/bootstrap.types'
import type { WorkspaceItem } from '../models/workspace.types'
import {
  hydrateAccountDetail,
  hydrateFeatureFlags,
  hydrateRbac,
  hydrateSessionAccess,
  hydrateWorkspacePreferences,
} from '../service/hydrateOwn'
import { PackageService, type PackageSignal } from '../service/PackageService'
import type { AccountStore } from '../stores/accountStore'
import type { WorkspaceAttribute, WorkspaceStore } from '../stores/workspaceStore'

import type { dispatchBootstrap } from './hydratorRegistry'

import { ApiError } from '@/shared/http'

// The account/user-scoped nav sections (BE-ACC-03/-05) ride the account-level dispatch: 07 owns
// no store for them — their writers are registered by the owning modules (08 nav/reports/
// schedules, 09 dashboards) via `registerBootstrapHydrator`; unregistered ones are skipped.
const ACCOUNT_LEVEL_SECTIONS: readonly BootstrapSection[] = [
  'session_access',
  'account_core',
  'account_detail',
  'rbac',
  'reports',
  'schedules',
  'integrations_connected',
  'dashboard_access',
]
/** Workspace-scoped sections. Exported because a switch RESETS these (plus compositions) before it
 *  re-hydrates (contract §7) — the reset list is the switch's business, the hydrate is shared. */
export const WORKSPACE_LEVEL_SECTIONS: readonly BootstrapSection[] = [
  'workspace_core',
  'dimensions',
  'integrations',
  'settings',
  'feature_flags',
]
/** Exported for the boot flow's DEFERRED dispatch and the switch's reset list. */
export const COMPOSITIONS_SECTIONS: readonly BootstrapSection[] = ['compositions']

/** The `finalize` package-signal sink — a pricing / limit / onboarding SIGNAL; the app stays usable
 *  either way (contract §14). Held BY REFERENCE (each flow hands over its own `hooks` object) so a
 *  hook read at call time behaves exactly as it did when `finalize` lived on the flow. */
export interface PackageSignalHooks {
  onPackageSignal?: (signal: PackageSignal) => void
}

export interface FlowToolkitOptions {
  accountStore: AccountStore
  workspaceStore: WorkspaceStore
  dispatch: typeof dispatchBootstrap
  /** Only the flows that own a `finalize` pass hooks; `SelectAccountFlow` delegates its finalize to
   *  `SelectWorkspaceFlow` and so never raises a signal from its own toolkit. */
  hooks?: PackageSignalHooks
}

export class FlowToolkit {
  private readonly accountStore: AccountStore
  private readonly workspaceStore: WorkspaceStore
  private readonly dispatch: typeof dispatchBootstrap
  private readonly hooks: PackageSignalHooks
  /** Dashboard count from the last workspace hydrate, an input to the package-limit signal (09's
   *  data, stashed rather than reached for via a `features/dashboards` import). One count per
   *  toolkit — i.e. per flow — exactly like the `lastDashboardCount` field it replaces. */
  private lastDashboardCount = 0

  constructor(options: FlowToolkitOptions) {
    this.accountStore = options.accountStore
    this.workspaceStore = options.workspaceStore
    this.dispatch = options.dispatch
    this.hooks = options.hooks ?? {}
  }

  /** `session_access` + `account_detail` + `rbac` → 07's own store; `account_core.workspaces` → the
   *  workspace list; everything else → the registered hydrators. `user`/`accounts` are re-hydrated,
   *  never wiped — an account switch keeps them (contract §7). */
  public hydrateAccountLevel(result: MappedBootstrapResult): void {
    hydrateSessionAccess(this.accountStore, result)
    hydrateAccountDetail(this.accountStore, result)
    hydrateRbac(this.accountStore, result)
    if (result.workspaces) {
      const workspaces = result.workspaces as unknown as WorkspaceItem[]
      const selectedId = this.workspaceStore.workspaceId
      this.workspaceStore.setWorkspaces(workspaces)

      // Account-context reloads include the fresh workspace rows, but they do not run the
      // workspace-switch commit. Keep the selected record aligned with that list so a workspace
      // rename (or timezone change) is visible immediately in Basics and the shell instead of
      // remaining stuck on the persisted object until the next full switch.
      if (selectedId !== undefined) {
        const selected = workspaces.find(
          (workspace) => workspace.id === selectedId || String(workspace.id) === String(selectedId),
        )
        if (selected) this.workspaceStore.setSelectedWorkspace(selected)
      }
    }
    this.dispatch(result, ACCOUNT_LEVEL_SECTIONS)
  }

  /** The workspace-scoped hydrate + the dashboard-count stash `finalize` reads. `feature_flags`
   *  → 07's own `workspaceStore` (BE-ACC-02/R2; explicit-empty writes, failed/cold skipped —
   *  see `hydrateFeatureFlags`). Compositions dispatch here only when the RESPONSE carried them —
   *  whether it does is the CALLER's business and the two callers differ deliberately: a switch
   *  always asks for compositions (eager), while the boot flow asks only on the Compositions
   *  route and otherwise fetches them deferred behind its own staleness guard. */
  public hydrateWorkspaceLevel(result: MappedBootstrapResult): void {
    if (result.attributes) {
      this.workspaceStore.setAttributes(result.attributes as WorkspaceAttribute[])
    }
    hydrateFeatureFlags(this.workspaceStore, result)
    const accountId = this.accountStore.accountId
    const workspaceId = this.workspaceStore.workspaceId
    if (accountId !== undefined && workspaceId !== undefined) {
      hydrateWorkspacePreferences(accountId, workspaceId, result)
    }
    this.lastDashboardCount = Array.isArray(result.dashboards) ? result.dashboards.length : 0
    this.dispatch(result, WORKSPACE_LEVEL_SECTIONS)
    if (result.compositions) this.dispatch(result, COMPOSITIONS_SECTIONS)
  }

  /**
   * Permissions + package + the dashboards-only FLAG (the route is 09's, ledger B9) + a package
   * SIGNAL (v1 `applyUserPackages` `:87-138`). The app stays `ready` in every case — degraded, not
   * blocked. Load-bearing on a workspace switch: `workspace_permissions[wid].permissions` OVERRIDE
   * the account's, so the effective RBAC changes with the workspace.
   *
   * The account-level base is the authoritative `rbac.permissions` (BE-ACC-01) when the section
   * landed. It is read from the store rather than the mapped result on purpose: `finalize` also
   * runs on a workspace switch and on the deferred-compositions re-run, neither of which re-fetches
   * `rbac` (it is account-scoped), so the account's warm payload must still apply.
   */
  public finalize(): void {
    const { permissions, packageId } = PackageService.computePermissionsAndPackage({
      detail: this.accountStore.accountDetail,
      workspaceId: this.workspaceStore.workspaceId,
      rbacPermissions: this.accountStore.rbac?.permissions ?? null,
    })

    this.accountStore.setPermissions(permissions)
    this.accountStore.setPackageId(packageId)
    this.workspaceStore.setCustomDashboardPermissions(this.computeOnlyDashboard())
    // The optional call MUST keep wrapping the computation rather than a pre-computed local: `?.()`
    // short-circuits its ARGUMENTS, so with no hook registered `computePackageSignal` never runs —
    // the shape both flows already had.
    this.hooks.onPackageSignal?.(
      PackageService.computePackageSignal({
        packageId,
        accountPackage: this.accountStore.accountDetail?.package,
        permissions,
        workspaceCount: this.workspaceStore.workspaces.length,
        connectedUserCount: this.accountStore.accountDetail?.connected_user_count ?? 0,
        dashboardCount: this.lastDashboardCount,
      }),
    )
  }

  /** No workspaces + no workspace access + has dashboard access → dashboards-only (v1 `:170-172`). */
  private computeOnlyDashboard(): boolean {
    const access = this.accountStore.userAccessInfo
    const hasNoWorkspaces = this.workspaceStore.workspaces.length < 1
    const hasNoAccessWorkspaces = (access?.workspaces ?? []).length < 1
    const hasAccessDashboards = (access?.dashboards ?? []).length > 0
    return hasNoWorkspaces && hasNoAccessWorkspaces && hasAccessDashboards
  }

  /**
   * Workspace pick order (v3 `pickWorkspaceFromStore`, `:455-477`) — ONE implementation with an
   * OPTIONAL preferred id: preferred → old-for-account → account-match → first. v3 called it both
   * ways and so do we: the boot flow passes the id it is booting, `SelectAccountFlow` passes none.
   * With no preferred id the first step is skipped whole, leaving the rest of the order untouched.
   *
   * `options.exclude` removes ONE id from every step (ORP-22838). The boot flow's
   * `WORKSPACE_NOT_FOUND` fallback is the only caller: without it the fallback re-picks the id
   * that just 404'd — `oldWorkspaceForAccount` still names it, and the list it is matched against
   * is the PERSISTED one whenever the re-fetched `account_core` came back degraded — so the
   * recovery re-issues the failing request and the second 404 lands on the error splash.
   */
  public pickWorkspace(
    preferredWsId?: number | string,
    options: { exclude?: number | string } = {},
  ): WorkspaceItem | null {
    const excluded = options.exclude
    const isExcluded = (id: number | string): boolean =>
      excluded !== undefined && String(id) === String(excluded)
    const workspaces = this.workspaceStore.workspaces.filter((w) => !isExcluded(w.id))
    if (!workspaces.length) return null
    if (preferredWsId !== undefined && !isExcluded(preferredWsId)) {
      const found = workspaces.find(
        (w) => w.id === preferredWsId || String(w.id) === String(preferredWsId),
      )
      if (found) return found
    }
    const accountId = this.accountStore.accountId
    if (accountId !== undefined) {
      const oldWsId = this.workspaceStore.oldWorkspaceForAccount[accountId]
      if (oldWsId !== undefined && !isExcluded(oldWsId)) {
        const found = workspaces.find((w) => w.id === oldWsId || String(w.id) === String(oldWsId))
        if (found) return found
      }
    }
    return workspaces.find((w) => w.account_id === accountId) ?? workspaces[0] ?? null
  }
}

/** `String(user.id ?? user.user.id ?? '')` — v3 key order preserved (id before user.id). */
export function userKeyOf(user: UserResponse | null): string {
  return String(user?.id ?? user?.user?.id ?? '')
}

/** Normalise to the one error type above `api/` (ledger A2). Never inspects a message. */
export function toApiError(error: unknown): ApiError {
  return ApiError.isApiError(error) ? error : ApiError.fromAxios(error)
}
